import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../../services/notifications.service';
import { BaseService } from '../../../../../services/base.service';
import { IntegrationService } from '../../../../../services/integration.service';
import { FormControl, FormGroup } from '@angular/forms';
import { SettingsForm } from './settings.interface';
import { SettingsService } from '../../../../../services/settings.service';
import { Router } from '@angular/router';
import { activeTranslations, PERMISSIONS } from '../../../../../enum/constants';
import { TranslateService } from '@ngx-translate/core';
import { CompanyService } from '../../../../../services/company.service';
import { LanguageService } from '../../../../../core/i18n';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SettingsComponent implements OnInit, OnDestroy {
  // Destroy relative
  private readonly destroy$: Subject<null> = new Subject<null>();
  public settingsForm!: FormGroup<SettingsForm>;
  public formSubmitted: boolean = false;
  public languages = [
    { name: 'Українська', value: 'ua' },
    { name: 'English', value: 'en' },
  ];

  constructor(
    private readonly notificationService: NotificationService,
    private readonly integrationService: IntegrationService,
    private readonly baseService: BaseService,
    public readonly settingsService: SettingsService,
    public readonly router: Router,
    private readonly languageService: LanguageService,
  ) {}

  ngOnInit() {
    this.baseService.checkPermission(PERMISSIONS.SETTINGS_ACCESS);
    this.initForm();
  }

  private initForm() {
    this.settingsForm = new FormGroup<SettingsForm>({
      is_notification: new FormControl(this.baseService.currentUserInfo.settings.is_notification),
      is_notification_sound: new FormControl(this.baseService.currentUserInfo.settings.is_notification_sound),
      is_message_read: new FormControl(this.baseService.currentUserInfo.settings.is_message_read),
      is_lazy_loading: new FormControl(this.baseService.currentUserInfo.settings.is_lazy_loading),
      language: new FormControl(this.baseService.currentUserInfo.settings.language),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  cancelChanges() {
    this.settingsForm.reset(this.baseService.currentUserInfo.settings);
  }

  isSave() {
    return !(
      this.settingsForm.controls.is_notification.value !== this.baseService.currentUserInfo.settings.is_notification ||
      this.settingsForm.controls.is_notification_sound.value !==
        this.baseService.currentUserInfo.settings.is_notification_sound ||
      this.settingsForm.controls.is_message_read.value !== this.baseService.currentUserInfo.settings.is_message_read ||
      this.settingsForm.controls.is_lazy_loading.value !== this.baseService.currentUserInfo.settings.is_lazy_loading ||
      this.settingsForm.controls.language.value !== this.baseService.currentUserInfo.settings.language
    );
  }

  public saveChanges() {
    this.formSubmitted = true;
    if (this.settingsForm.valid) {
      const language = this.settingsForm.controls.language.value;

      this.settingsService
        .updateSettings(this.settingsForm.getRawValue())
        .pipe(takeUntil(this.destroy$))
        // Мова застосовується «на льоту»: без window.location.reload().
        .pipe(switchMap(() => this.languageService.setLanguage(language)))
        .pipe(switchMap(() => this.baseService.getProfile()))
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_updated,
            );
          },
          error: (res) => {
            (res.error?.errors || []).forEach((err: any) => {
              this.notificationService.showError('', err.message);
            });
          },
        });
    }
  }
}
